import Vue from "vue"

import { isPresent, isEmpty } from "@/lib/utils"
import ApiClient from "@/lib/api-client"
import {
  buildMessageId,
  buildMessageFromPusherData,
  groupMessages,
  MESSAGE_ORDER
} from "@/lib/messages-utils"

import {
  subscribeToThread,
  unsubscribeFromThread,
  publishToThread,
  removeFromThread,
  reactMessage,
  MESSAGE_TYPES,
  REACTION_TYPES
} from "@/lib/pusher-utils"

const getInitialState = () => ({
  messageThreads: {},
  messages: {},
  messagesGrouped: {},
  replyingMessages: {},
  adminThread: null,
  pusherSubscriptions: {},
  participants: {},
  globalUnreadStatus: false,
  globalUnreadStatusChannel: false
})

const handleCallback = (state, messageThreadId, event) => message =>
  Object.values(
    state.pusherSubscriptions[messageThreadId]?.callbacks || {}
  ).map(cb => (cb[event] ? cb[event](message) : null))

const setupThreadSubscription = (state, commit, messageThreadId) => {
  const messageThread = state.messageThreads[messageThreadId]

  return subscribeToThread(messageThread.channel_id, {
    onMessage: event => {
      commit(
        "pushMessageToThread",
        buildMessageFromPusherData(messageThreadId, event)
      )

      return handleCallback(state, messageThreadId, "onMessage")(event)
    },
    onMessageDeleted: event => {
      commit("deleteMessageFromThread", {
        messageThreadId,
        message: buildMessageFromPusherData(messageThreadId, event)
      })

      return handleCallback(state, messageThreadId, "onMessageDeleted")(event)
    },
    onMessageUpdated: event => {
      commit(
        "updateMessage",
        buildMessageFromPusherData(messageThreadId, event)
      )

      return handleCallback(state, messageThreadId, "onMessageUpdated")
    },
    onMessageReaction: event => {
      commit("updateMessageReactions", {
        messageThreadId,
        data: {
          action: event.action,
          userId: event.user.id,
          messageTs: event.messageTs
        }
      })

      return handleCallback(state, messageThreadId, "onMessageReaction")
    },
    onSubscribe: handleCallback(state, messageThreadId, "onSubscribe"),
    onMemberAdded: handleCallback(state, messageThreadId, "onMemberAdded"),
    onMemberRemoved: handleCallback(state, messageThreadId, "onMemberRemoved")
  })
}

export default {
  namespaced: true,

  state: getInitialState(),

  mutations: {
    setMessageThreads(state, { message_threads }) {
      if (message_threads)
        state.messageThreads = [...state.messageThreads, ...message_threads]
    },

    setReplyingMessage(state, { threadId, message }) {
      Vue.set(state.replyingMessages, threadId, message)
    },

    setAdminThread(state, { thread_id, subscriber_id }) {
      state.adminThread = { thread_id, subscriber_id }
    },

    setMessageThread(state, messageThread) {
      Vue.set(state.messageThreads, messageThread.id, {
        ...(state.messageThreads[messageThread.id] || {}),
        ...messageThread
      })
    },

    setGlobalUnreadStatus(state, hasUnread) {
      state.globalUnreadStatus = hasUnread
    },

    setGlobalUnreadStatusChannel(state, channel) {
      state.globalUnreadStatusChannel = channel
    },

    setMessages(state, { messageThreadId, messages }) {
      Vue.set(state.messages, messageThreadId, {
        ...(state.messages[messageThreadId] || {}),
        ...messages.reduce((acc, m) => {
          acc[parseInt(m.message_ts)] = m
          return acc
        }, {})
      })

      Vue.set(
        state.messagesGrouped,
        messageThreadId,
        groupMessages(Object.values(state.messages[messageThreadId]))
      )
    },

    setParticipant(state, { messageThreadId, participant }) {
      const _participant = state.participants[messageThreadId] || {
        liked_messages: [],
        user: {}
      }

      Vue.set(state.participants, messageThreadId, {
        ..._participant,
        ...participant
      })
    },

    setPusherSubscription(
      state,
      { messageThreadId, subscription, subscriptionId, callbacks }
    ) {
      const existingSubscription = state.pusherSubscriptions[messageThreadId]
      const args = {
        subscription,
        callbacks: existingSubscription?.callbacks || {}
      }

      if (
        !existingSubscription ||
        !existingSubscription.callbacks[subscriptionId]
      ) {
        args.callbacks[subscriptionId] = callbacks
      }

      // Because we are re-using the Pusher subscription, we need to mock the
      // callback for onSubscribe otherwise it can cause it to not be called
      if (
        existingSubscription &&
        callbacks["onSubscribe"] &&
        existingSubscription.subscription.presenceChannel
      ) {
        callbacks["onSubscribe"](
          existingSubscription.subscription.presenceChannel.members
        )
      }

      Vue.set(state.pusherSubscriptions, messageThreadId, args)
    },

    removeSubscriptionCallbacks(state, { messageThreadId, subscriptionId }) {
      if (
        state.pusherSubscriptions[messageThreadId] &&
        state.pusherSubscriptions[messageThreadId].callbacks[subscriptionId]
      ) {
        delete state.pusherSubscriptions[messageThreadId].callbacks[
          subscriptionId
        ]
      }
    },

    setSubscriberForNewChat(state, subscriber) {
      state.subscriberForNewChat = subscriber
    },

    pushMessageToThread(state, message) {
      message.reaction_count = 0

      const thread = state.messageThreads[message.message_thread_id]

      const threadArgs = {
        message_count: (thread.message_count || 0) + 1,
        latest_message_ts: message.message_ts,
        latest_message: message
      }

      if (
        message.user?.id &&
        !(thread.participants || []).find(p => p.user.id === message.user.id)
      ) {
        threadArgs.participants = [
          ...(thread.participants || []),
          { user: message.user }
        ]
      }

      Vue.set(state.messageThreads, message.message_thread_id, {
        ...thread,
        ...threadArgs
      })

      this.commit("messages/setMessages", {
        messageThreadId: message.message_thread_id,
        messages: [message]
      })

      const participant = state.participants[message.message_thread_id]

      if (
        isPresent(message.user) &&
        isPresent(participant.user) &&
        message.user.id === participant.user.id &&
        !participant.has_contributed
      ) {
        this.commit("messages/setParticipant", {
          messageThreadId: message.message_thread_id,
          participant: {
            ...participant,
            has_contributed: true
          }
        })
      }
    },

    pushMessageThread(state, { messageThread }) {
      const messageThreads = state.messageThreads.filter(
        currentMessageThread => currentMessageThread.id !== messageThread.id
      )

      messageThreads.unshift(messageThread)

      state.messageThreads = messageThreads
    },

    updateMessageReactions(state, { messageThreadId, data }) {
      let messageToUpdate = state.messages[messageThreadId][data.messageTs]

      if (!messageToUpdate) return

      switch (data.action) {
        case REACTION_TYPES.LIKE:
          messageToUpdate.reaction_count += 1

          break
        case REACTION_TYPES.UNLIKE:
          messageToUpdate.reaction_count += -1

          break
        default:
          return
      }
    },

    updateMessage(state, message) {
      Vue.set(
        state.messages[message.message_thread_id],
        message.message_ts,
        message
      )

      Vue.set(
        state.messagesGrouped,
        message.message_thread_id,
        groupMessages(Object.values(state.messages[message.message_thread_id]))
      )
    },

    deleteMessageFromThread(state, { messageThreadId, message }) {
      if (
        state.messages[messageThreadId] &&
        state.messages[messageThreadId][message.message_ts]
      ) {
        Vue.delete(state.messages[messageThreadId], message.message_ts)

        Vue.set(
          state.messagesGrouped,
          messageThreadId,
          groupMessages(Object.values(state.messages[messageThreadId]))
        )
      }

      const thread = state.messageThreads[messageThreadId]
      const messages = state.messages[messageThreadId]

      const newThreadArgs = {
        ...thread,
        message_count: (thread.message_count || 1) - 1
      }

      if (thread.latest_message_ts === message.message_ts) {
        const latestMessage = Object.keys(messages).sort((a, b) => b - a)[0]

        newThreadArgs.latest_message_ts = latestMessage
        newThreadArgs.latest_message = messages[latestMessage]
      }

      Vue.set(state.messageThreads, message.message_thread_id, newThreadArgs)
    },

    reset(state) {
      if (state.globalUnreadStatusChannel) {
        unsubscribeFromThread(state.globalUnreadStatusChannel)
      }

      // Merge rather than replace so we don't lose observers
      // https://github.com/vuejs/vuex/issues/1118
      Object.assign(state, getInitialState())
    }
  },

  actions: {
    async subscribeToThread(
      { state, dispatch, commit },
      { subscriptionId, messageThreadId, callbacks = {} }
    ) {
      if (!isPresent(subscriptionId)) throw "ID required to identify callbacks"

      if (!state.messageThreads[messageThreadId]) {
        await dispatch("fetchMessageThread", messageThreadId)
      }

      const existingSubscription =
        state.pusherSubscriptions[messageThreadId]?.subscription

      const subscription =
        existingSubscription ||
        setupThreadSubscription(state, commit, messageThreadId)

      commit("setPusherSubscription", {
        messageThreadId,
        subscription,
        subscriptionId,
        callbacks
      })

      return subscription
    },

    unsubscribeFromThread({ commit }, { subscriptionId, messageThreadId }) {
      commit("removeSubscriptionCallbacks", {
        messageThreadId,
        subscriptionId
      })
    },

    publishToThread({ state, commit }, { messageThreadId, message }) {
      const subscription =
        state.pusherSubscriptions[messageThreadId].subscription

      publishToThread({ channel: subscription.channel, ...message })

      commit(
        "pushMessageToThread",
        buildMessageFromPusherData(messageThreadId, message)
      )
    },

    deleteMessage({ state, commit }, { message, sessionId }) {
      const subscription =
        state.pusherSubscriptions[message.message_thread_id].subscription

      removeFromThread({
        channel: subscription.channel,
        user: message.user,
        messageTs: message.message_ts,
        messageType: message.message_type,
        sessionId: sessionId
      })

      commit("deleteMessageFromThread", {
        messageThreadId: message.message_thread_id,
        message
      })
    },

    reactMessage(
      { state, commit, getters },
      { message, action, likedUserId, sessionId }
    ) {
      const subscription =
        state.pusherSubscriptions[message.message_thread_id].subscription

      reactMessage({
        channel: subscription.channel,
        user: message.user,
        messageTs: message.message_ts,
        messageType: MESSAGE_TYPES.MESSAGE_REACTION,
        action,
        likedUserId,
        sessionId
      })

      commit("updateMessageReactions", {
        messageThreadId: message.message_thread_id,
        data: {
          userId: message.user?.id,
          messageTs: message.message_ts,
          action
        }
      })

      const messageToUpdate = getters.getMessage(
        message.message_thread_id,
        message.message_ts
      )

      const participant = getters.getParticipant(message.message_thread_id)

      let likedMessages =
        action === REACTION_TYPES.LIKE
          ? participant.liked_messages.concat(messageToUpdate)
          : participant.liked_messages.filter(
              m => m.message_ts !== messageToUpdate.message_ts
            )

      commit("setParticipant", {
        messageThreadId: message.message_thread_id,
        participant: { liked_messages: likedMessages }
      })
    },

    fetchMessageThreads({ commit }, page = 1) {
      return ApiClient.get(`/threads?page=${page}`).then(res => {
        const { message_threads } = res.data

        message_threads.forEach(thread => {
          commit("setMessageThread", thread)

          commit("setParticipant", {
            messageThreadId: thread.id,
            participant: thread.participant
          })

          if (thread.parent_message) {
            commit("setMessages", {
              messageThreadId: thread.id,
              messages: [thread.parent_message]
            })
          }
        })

        const { thread_id, subscriber_id } = res.data.admin_thread
        commit("setAdminThread", { thread_id, subscriber_id })

        return res.data.message_threads
      })
    },

    fetchMessageThread({ commit }, messageThreadId) {
      return ApiClient.get(`/threads/${messageThreadId}`).then(res => {
        const thread = res.data.message_thread
        commit("setMessageThread", thread)
        commit("setParticipant", {
          messageThreadId,
          participant: thread.participant
        })

        if (thread.parent_message) {
          commit("setMessages", {
            messageThreadId: thread.parent_message.message_thread_id,
            messages: [thread.parent_message]
          })
        }

        return res.data
      })
    },

    fetchMessage(_, { messageThreadId, messageTs }) {
      return ApiClient.get(
        `/threads/${messageThreadId}/messages/${messageTs}`
      ).then(res => {
        return res.data.message
      })
    },

    fetchMessages(
      { commit },
      {
        messageThreadId,
        after = null,
        before = null,
        order = "desc",
        parentTs = null
      }
    ) {
      return ApiClient.get(`/threads/${messageThreadId}/messages`, {
        params: {
          before,
          after,
          order,
          parent_ts: parentTs
        }
      }).then(res => {
        commit("setMessages", {
          messageThreadId,
          messages: res.data.messages
        })
        commit("setMessageThread", {
          id: messageThreadId,
          remaining_messages: res.data.remaining_messages
        })
      })
    },

    fetchMessageReactions(_, { messageThreadId, messageTs }) {
      return ApiClient.get(
        `/threads/${messageThreadId}/messages/${messageTs}/reactions`
      ).then(res => res.data)
    },

    createMessageThread(
      { state, commit },
      { subscriberId, parentMessage, messages = [] }
    ) {
      const messageParams = messages.map(message => {
        let params = {
          message_type: message.messageType,
          created_at: message.messageCreatedAt,
          message_ts: parseInt(message.messageCreatedAt)
        }

        if (message.messageType === MESSAGE_TYPES.MESSAGE) {
          params.message = message.messageBody
        } else if (message.messageType === MESSAGE_TYPES.IMAGE) {
          params.image_ids = message.imageIds
        }

        return params
      })

      return ApiClient.post(`/threads`, {
        subscriber_id: subscriberId,
        parent_message_id: isPresent(parentMessage)
          ? buildMessageId(parentMessage)
          : null,
        messages: messageParams
      }).then(res => {
        const { message_thread } = res.data

        commit("setMessageThread", message_thread)

        if (parentMessage) {
          commit("setMessages", {
            messageThreadId: parentMessage.message_thread_id,
            messages: [
              {
                ...parentMessage,
                reply_thread_id: message_thread.id
              }
            ]
          })
        }

        // Set the admin thread if it was just created
        if (
          message_thread.is_private &&
          message_thread.message_to_subscriber &&
          state.adminThread &&
          message_thread.message_to_subscriber.id ===
            state.adminThread.subscriber_id
        ) {
          commit("setAdminThread", {
            thread_id: message_thread.id,
            subscriber_id: message_thread.message_to_subscriber.id
          })
        }

        return message_thread
      })
    },

    fetchSubscriber(_, subscriberId) {
      return ApiClient.get(`/subscribers/${subscriberId}`).then(res => {
        return res.data.subscriber
      })
    },

    fetchGlobalUnreadStatus({ commit }) {
      return ApiClient.get(`/threads/unread_status`).then(res => {
        commit("setGlobalUnreadStatus", !!res.data.has_unread)
      })
    },

    setMessageThread({ commit }, data) {
      commit("setMessageThread", data)
    },

    setSubscriberForNewChat({ commit }, data) {
      commit("setSubscriberForNewChat", data)
    },

    setGlobalUnreadStatusChannel({ commit }, channel) {
      commit("setGlobalUnreadStatusChannel", channel)
    },

    toggleNotification({ commit }, { messageThreadId, active }) {
      return ApiClient.post(`/threads/${messageThreadId}/toggle_notification`, {
        active
      }).then(res => {
        const active = res.data?.active

        commit("setParticipant", {
          messageThreadId: messageThreadId,
          participant: {
            notifications_enabled: active
          }
        })

        return active
      })
    },

    markRead({ commit }, { messageThreadId, clientTs = +new Date() }) {
      commit("setMessageThread", {
        id: messageThreadId,
        is_unread: false,
        last_message_read_ts: clientTs
      })

      return ApiClient.get(
        `threads/${messageThreadId}/read?client_ts=${clientTs}`
      )
    },

    initializeGlobalUnreadChannel({ state, commit }, channel) {
      if (!state.globalUnreadStatusChannel) {
        const globalChannel = subscribeToThread(
          channel,
          {
            onMessage: () => {
              commit("setGlobalUnreadStatus", true)
            }
          },
          false
        )

        commit("setGlobalUnreadStatusChannel", globalChannel)
      }
    }
  },

  getters: {
    getReplyingMessageTs(_, getters) {
      return threadId => {
        const replyingMessge = getters.getReplyingMessage(threadId)
        return replyingMessge ? replyingMessge.message_ts : null
      }
    },

    getReplyingMessage(state) {
      return threadId => state.replyingMessages[threadId]
    },

    getMessages(state) {
      return ({
        threadId,
        order = MESSAGE_ORDER.LATEST_FIRST,
        grouped = false
      }) => {
        if (isEmpty(state.messages[threadId])) return []

        if (grouped) {
          const messageGroups = state.messagesGrouped[threadId]
          const messageGroupKeys = Object.keys(messageGroups)

          const orderedGroupings =
            order === MESSAGE_ORDER.OLDEST_FIRST
              ? messageGroupKeys.sort((a, b) => a - b)
              : messageGroupKeys.sort((a, b) => b - a)

          return orderedGroupings.map(key => messageGroups[key])
        } else {
          const messages = Object.values(state.messages[threadId])

          return order === MESSAGE_ORDER.OLDEST_FIRST
            ? messages.sort((a, b) => a.message_ts - b.message_ts)
            : messages.sort((a, b) => b.message_ts - a.message_ts)
        }
      }
    },

    getMessagesByGroupTs(_, getters) {
      return ({ threadId, groupTs, order = MESSAGE_ORDER.LATEST_FIRST }) =>
        (getters.getMessages({ threadId, order }) || []).filter(
          m => m.group_ts === groupTs
        )
    },

    getMessage(state) {
      return (threadId, messageTs) => {
        if (isEmpty(state.messages[threadId])) return null

        return state.messages[threadId][parseInt(messageTs)]
      }
    },

    getMessageThreads(state) {
      return Object.values(state.messageThreads)
    },

    getLastMessage(state) {
      return messageThreadId => {
        return isPresent(state.messages[messageThreadId])
          ? state.messages[messageThreadId][
              state.messages[messageThreadId].length - 1
            ]
          : null
      }
    },

    getMessageThread(state) {
      return messageThreadId => state.messageThreads[messageThreadId]
    },

    getParticipant(state) {
      return messageThreadId => state.participants[messageThreadId]
    },

    hasUnreadMessageThreads(state) {
      return !!Object.values(state.messageThreads).find(
        thread => thread.is_unread
      )
    },

    isUnreadAt(_, getters) {
      return (messageThreadId, ts) => {
        const thread = getters.getMessageThread(messageThreadId)

        if (!thread) return false
        if (!thread.last_message_read_ts) return true

        return ts > thread.last_message_read_ts
      }
    },

    isReplying(_, getters) {
      return threadId => !!getters.getReplyingMessage(threadId)
    }
  }
}
